import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { videoCategories, getVideosByCategory, VideoItem } from '@/mocks/videos';
import { siteInfo } from '@/mocks/site';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';

const VIDEOS_PER_PAGE = 12;

function formatViews(views: number): string {
  if (views >= 100000000) return `${(views / 100000000).toFixed(1)}亿`;
  if (views >= 10000) return `${(views / 10000).toFixed(0)}万`;
  return views.toString();
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [currentPage, setCurrentPage] = useState(1);

  const currentCategory = videoCategories.find((c) => c.slug === slug);
  const categoryName = currentCategory?.name || slug || '未知分类';

  const allCategoryVideos = useMemo(() => {
    if (!slug) return [];
    return getVideosByCategory(slug);
  }, [slug]);

  const totalPages = Math.ceil(allCategoryVideos.length / VIDEOS_PER_PAGE);
  const displayedVideos = allCategoryVideos.slice(
    (currentPage - 1) * VIDEOS_PER_PAGE,
    currentPage * VIDEOS_PER_PAGE
  );

  const webPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - ${siteInfo.fullName}`,
    description: currentCategory?.description || `${categoryName}视频在线观看`,
    url: `${siteInfo.url}/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.fullName,
      url: siteInfo.url,
    },
  };

  const handleCategoryChange = (newSlug: string) => {
    setCurrentPage(1);
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageJsonLd) }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="container-main">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName },
              ]}
            />
            <div className="pb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                {categoryName}
              </h1>
              {currentCategory?.description && (
                <p className="text-sm text-foreground-500 max-w-2xl">
                  {currentCategory.description}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-background-50 border-b border-background-200/70">
          <div className="container-main py-3">
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              {videoCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800'
                  }`}
                >
                  <i className={cat.icon}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <section className="py-10 bg-background-50">
          <div className="container-main">
            {displayedVideos.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {displayedVideos.map((video) => (
                    <Link
                      key={video.id}
                      to={`/category/${video.category}`}
                      className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-background-300 transition-all duration-300"
                    >
                      <div className="relative aspect-video overflow-hidden bg-background-200">
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          title={`${video.title} - 茄子视频在线观看`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/70 text-white text-xs rounded">
                          {video.duration}
                        </div>
                        <div className="absolute top-2 left-2 px-2 py-0.5 bg-primary-500 text-white text-xs rounded">
                          {video.resolution}
                        </div>
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                          <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <i className="ri-play-fill text-primary-600 text-xl ml-0.5"></i>
                          </div>
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors">
                          {video.title}
                        </h3>
                        <div className="flex items-center justify-between text-xs text-foreground-500">
                          <div className="flex items-center gap-1">
                            <i className="ri-eye-line"></i>
                            <span>{formatViews(video.views)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <i className="ri-star-fill text-accent-500 text-xs"></i>
                            <span>{video.rating}</span>
                          </div>
                        </div>
                        {video.tags && video.tags.length > 0 && (
                          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                            {video.tags.slice(0, 2).map((tag) => (
                              <span
                                key={tag}
                                className="px-2 py-0.5 bg-secondary-50 text-secondary-700 text-xs rounded"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-10">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-medium transition-colors ${
                          currentPage === page
                            ? 'bg-primary-500 text-white'
                            : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
                  <i className="ri-video-line text-3xl text-foreground-400"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-700 mb-2">
                  暂无视频内容
                </h3>
                <p className="text-sm text-foreground-500 mb-6">
                  该分类下暂时没有视频，请浏览其他分类
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors"
                >
                  <i className="ri-home-4-line"></i>
                  返回首页
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}